<script lang="ts" setup>
import type { OfferProperty, OfferSmall } from '~/types/Catalog';

const isShowOffersModal = defineModel<boolean>({ default: false });

const { selectedOffer, selectedSizeId } = storeToRefs(useCatalogStore());
const { isMobile } = useBreakpoints();

const isShowSelectField = computed(() => {
  return (
    selectedOffer.value.offers.length &&
    selectedOffer.value.offers.some((offer) => offer.offer_balances.some((balance) => balance.balance > 0))
  );
});

const selectedSmallOffer = ref<OfferSmall | null>();

watch(
  () => selectedSmallOffer.value,
  () => {
    if (selectedSmallOffer.value?.id) {
      return navigateTo(selectedSmallOffer.value.uri);
    }
  },
);

const sortedOffers = computed(() => {
  return selectedOffer.value.offers
    .map((offer) => ({ ...offer, isDisabled: offer.balance !== undefined && offer.balance < 1 }))
    .sort((a, b) => a.offer_name.localeCompare(b.offer_name));
});

const sizesColumnLabels = computed(() => {
  let labels = [];

  if (sortedOffers.value[0]?.sizes?.length) {
    labels = sortedOffers.value[0]?.sizes;
  } else {
    const sizeLabel = selectedOffer.value.offers[0].offer_properties?.find((p: OfferProperty) =>
      p.name.includes('Размер'),
    );
    if (sizeLabel) labels.push(sizeLabel);
  }

  return labels;
});

function handleClickOnSize(offer: OfferSmall) {
  selectedSmallOffer.value = offer;
  isShowOffersModal.value = false;
}

function handleClick() {
  if (isMobile.value) {
    isShowOffersModal.value = true;
  }
}

onMounted(
  () => (selectedSmallOffer.value = selectedOffer.value.offers.find((offer) => offer.id === selectedOffer.value.id)),
);

onUnmounted(() => {
  selectedSizeId.value = null;
});
</script>

<template>
  <section class="product-page__offers">
    <SelectField
      v-if="isShowSelectField"
      :id="selectedOffer.id"
      v-model="selectedSmallOffer"
      :allow-empty="false"
      :options="sortedOffers"
      field-label="Выбрать вариант"
      :searchable="false"
      class="mt-4"
      placeholder="Выберите вариант"
      track-by="offer_name"
      @click="handleClick"
    >
      <template #singleLabel="{ option }">
        <div class="flex gap-2 items-center">
          <picture
            v-if="option.offer_images?.length"
            class="size-5"
          >
            <source :srcset="option.offer_images[0]?.conversions_webp?.thumb_url_65" />
            <img
              :src="option.offer_images[0].url"
              alt=""
              class="size-full object-contain"
            />
          </picture>

          {{ option.offer_name }}
        </div>
      </template>

      <template #option="{ option }">
        <div class="flex gap-2 items-center">
          <picture
            v-if="option.offer_images?.length"
            class="size-5"
          >
            <source :srcset="option.offer_images[0].conversions_webp.thumb_url_65" />
            <img
              :src="option.offer_images[0].url"
              class="size-full object-contain"
              alt=""
            />
          </picture>

          {{ option.offer_name }}
        </div>
      </template>
    </SelectField>

    <BaseModal
      v-model="isShowOffersModal"
      confirm-btn-icon="Cart"
      title="Выберите вариант"
      @close="isShowOffersModal = false"
    >
      <button
        v-for="smallOffer in sortedOffers"
        :key="smallOffer.id"
        :disabled="smallOffer.isDisabled"
        class="product-page__offers-select-row product-page__modal-offer-list-item py-2 last:mb-2"
        @click="handleClickOnSize(smallOffer)"
      >
        <picture
          v-if="smallOffer.offer_images?.length"
          class="size-5"
        >
          <source :srcset="smallOffer.offer_images[0].conversions_webp.thumb_url_65" />
          <img
            :src="smallOffer.offer_images[0].url"
            class="size-full object-contain"
            alt=""
          />
        </picture>

        {{ smallOffer.offer_name }}
      </button>
    </BaseModal>
  </section>
</template>

<style lang="scss" scoped>
.product-page {
  &__offers {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__offers-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__offers-btn {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    min-width: 64px;
    min-height: 40px;
    border: 1px solid transparent;
    border-radius: 12px;
    background-color: $white;
    transition: all 0.3s;

    @apply shadow-sm;

    &:hover {
      border-color: $black-20;
    }

    &--selected {
      border-color: $black-60;
    }
  }

  &__offers-select-row {
    display: flex;
    gap: 8px;
    width: 100%;
    font-size: $text-sm;
    text-align: left;
  }

  &__modal-offer-list-item {
    padding: 8px;
    border-bottom: 1px solid $black-20;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      background-color: $black-5;
      color: $black-60;
    }

    &:disabled {
      background-color: $black-5;
      color: $black-40;
      cursor: not-allowed;
    }

    &:last-of-type {
      border: none;
    }
  }
}
</style>
